import { CaretsInVertical, CaretsOutVertical, ChevronLeft, Clear, EllipsisVertical } from "@procore/core-icons";
import { Box, Button, Card, colors, Menu, OverlayTrigger, spacing, Tooltip, Typography } from "@procore/core-react";
import { ComponentProps, createContext, CSSProperties, ReactNode, useCallback, useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { sidePanelsActions, useDispatchSidePanels, useIsCollapsed } from "contexts/sidePanels";
import ExpandIcon from "media/icons/expand.svg?react";
import MinimizeIcon from "media/icons/minimize.svg?react";
import { useIsTruncated } from "utils/truncatedText";

import { HEADER_BORDER, SLOTTED_WINDOW_HEADER_PADDING } from "./constants";
import { DRAWER_Z_INDEX } from "./drawer/constants";
import { SidePanelFabTargetContext } from "./sidePanel/sidePanelFabTargetContext";
import type { EmptyStateConfig } from "./types";
import { useWindowPlatformMode } from "./useWindowPlatformMode";
import { Window } from "./window";
import { useWindowMinimize } from "./windowMinimizeContext";
import { WindowPosition } from "./windowPositioning";

// `ButtonVariant` is not re-exported by @procore/core-react — derive the
// chrome-specific variant type from Button's props.
type ChromeButtonVariant = NonNullable<React.ComponentProps<typeof Button>["variant"]>;

// Internal context so helper components (Title) can adapt their rendering to
// minimize state without consumers having to plumb it through. Intentionally
// not exported — consumers should stay ignorant of minimize.
interface SlottedWindowInternalContext {
    minimized: boolean;
}
const SlottedWindowContext = createContext<SlottedWindowInternalContext>({ minimized: false });

interface SlottedWindowMenuItem {
    label: string;
    onClick: () => void;
    disabled?: boolean;
}

interface SlottedWindowActionBase {
    icon: ReactNode;
    /** Tooltip text + overflow menu label + a11y name. */
    label: string;
    /**
     * Button variant. Defaults to `"tertiary"`. Use `"primary"` for the emphasized
     * create/orange action (Viewpoints' `+`). Ignored when the action ends up in
     * an overflow menu — overflow items render as plain menu rows regardless.
     */
    variant?: "primary" | "tertiary";
    disabled?: boolean;
    "data-testid"?: string;
}

/**
 * A single header action. Pass one or more in the `actions` prop.
 *
 * Two shapes — provide `onClick` or `items`, not both:
 *   - `onClick` — fires a callback directly on click.
 *   - `items`   — opens a dropdown of sub-items on click.
 */
export type SlottedWindowAction =
    | (SlottedWindowActionBase & { onClick: () => void; items?: never })
    | (SlottedWindowActionBase & { items: SlottedWindowMenuItem[]; onClick?: never });

export interface SlottedWindowProps {
    /**
     * Consumer-owned header content. Renders left of novoweb's pinned
     * `[collapse][action?][close]` cluster. Compose with the exported helpers
     * (`SlottedWindow.HeaderRow`, `.Title`, `.BackButton`) for the default
     * look, or drop in raw JSX for exotic patterns (wizard steps, view-driven
     * titles).
     */
    headerRegion: ReactNode;
    /**
     * Custom header actions. Rendered between the pinned collapse and close
     * buttons. Design rules (desktop):
     *   - 0 actions: header is `[collapse][close]`
     *   - 1 action:  header is `[collapse][action][close]` — inline
     *   - 2+ actions: header is `[collapse][⋮ overflow][close]` — every action
     *     collapses into a single kebab menu (design: two buttons can never be
     *     shown at once). Actions with `items` are flattened — their sub-items
     *     appear as rows directly in the kebab, the parent label is dropped.
     *
     * A single action with `items` (instead of `onClick`) renders as an inline
     * dropdown button — clicking it opens a menu of its `items`. This is the
     * Viewpoints "+" create pattern: one button, multiple creation modes.
     * In drawer/mobile mode, a `variant: "primary"` action is automatically
     * promoted to a floating action button (FAB) portaled above the panel chrome.
     */
    actions?: SlottedWindowAction[];
    /**
     * Sticky content rendered between the header and the body. Hidden while
     * minimized. Compose with `SlottedWindow.TabsRow` / `.SubheaderRow` /
     * `.BulkActionsRow` for consistent padded rows, or drop in raw JSX.
     */
    subheaderRegion?: ReactNode;
    children: ReactNode;
    /**
     * `BaseWindowProps.onClose` — supplied by `WindowManager`. Renders the
     * pinned close button.
     */
    onClose?: () => void;
    /**
     * `BaseWindowProps.position` — supplied by `WindowManager`. Passed through
     * so drag position round-trips to Redux state.
     */
    position?: WindowPosition;
    loading?: boolean;
    loadingMessage?: string;
    error?: string | null;
    emptyState?: EmptyStateConfig;
    size?: { width: number; height?: number };
    minSize?: { width?: number; height?: number };
    /**
     * Paints the window body with `colors.gray96` instead of white. Use for
     * windows whose content rows already have a white card/row background and
     * need the contrast (Properties, Dimension).
     */
    secondaryBgColor?: boolean;
    /**
     * Shrinks the Rnd shell to content height instead of a fixed height.
     * Use for windows whose content varies in length (Settings).
     */
    autoHeight?: boolean;
    /**
     * Window chrome rendering mode. Defaults to `"full"`.
     *
     * Use `"headless"` when the body provides its own complete header. This
     * removes the consumer header slot, pinned chrome buttons, subheader, and
     * drawer FAB; the body must provide any required close or navigation controls.
     */
    chromeVariant?: "full" | "headless";
    "data-testid"?: string;
}

/**
 * Adds collapse state management on top of `useWindowPlatformMode`.
 * Routes the collapse button to the correct source depending on platform:
 *   - Docked:   DockedWindow's `WindowMinimizeContext` → Redux
 *   - Mobile:   `sidePanels` Redux slice (drives SidePanel half-height snap)
 *   - Floating: local `useState`, passed to Window's `minimized` prop
 */
type CollapseVariant =
    | "drawer" // Drawer has its own chrome — no collapse button shown
    | "mobilePanel" // SidePanel: collapse shrinks container, content stays mounted, state in Redux
    | "dockedPanel" // Docked slot: collapse hides content, state in WindowMinimizeContext
    | "floatingWindow"; // Free-floating: collapse hides content, state is local

function useWindowChromeState() {
    const platform = useWindowPlatformMode();
    const { isDocked, isMobile, isDrawer } = platform;

    // All hooks called unconditionally (Rules of Hooks) even if unused for this variant
    const sidePanelCollapsed = useIsCollapsed();
    const dispatchSidePanels = useDispatchSidePanels();
    const dockedCtx = useWindowMinimize();
    const [floatingMinimized, setFloatingMinimized] = useState(false);

    const collapseVariant: CollapseVariant = isDrawer
        ? "drawer"
        : isMobile
          ? "mobilePanel"
          : isDocked
            ? "dockedPanel"
            : "floatingWindow";

    const isCollapsed = (() => {
        switch (collapseVariant) {
            case "mobilePanel":
                return sidePanelCollapsed;
            case "dockedPanel":
                return dockedCtx?.minimized ?? false;
            case "floatingWindow":
                return floatingMinimized;
            case "drawer":
                return false;
        }
    })();

    // mobilePanel collapses the SidePanel container via CSS — content stays
    // mounted. All other variants hide the body entirely.
    const hideContent = collapseVariant !== "mobilePanel" && collapseVariant !== "drawer" && isCollapsed;

    const toggleCollapse = useCallback(() => {
        switch (collapseVariant) {
            case "mobilePanel":
                dispatchSidePanels(sidePanelsActions.toggleCollapsed());
                break;
            case "dockedPanel":
                dockedCtx?.onToggle();
                break;
            case "floatingWindow":
                setFloatingMinimized((v) => !v);
                break;
        }
    }, [collapseVariant, dockedCtx, dispatchSidePanels]);

    return {
        ...platform,
        collapseVariant,
        isCollapsed,
        hideContent,
        toggleCollapse,
        setLocalMinimized: setFloatingMinimized,
    };
}

interface WindowHeaderProps {
    headerRegion: ReactNode;
    subheaderRegion?: ReactNode;
    actions?: SlottedWindowAction[];
    onClose?: () => void;
    chromeState: ReturnType<typeof useWindowChromeState>;
}

/**
 * Mounts the two consumer slots and wraps them with the pinned chrome buttons.
 * Does not define what goes inside the slots — that is entirely up to the consumer.
 *
 * Layout:
 *   [ headerRegion (consumer JSX) ]  [ collapse ][ actions ][ close ]
 *   [ subheaderRegion (consumer JSX, optional)                       ]
 *
 * Extracted from `SlottedWindowRoot` so the root stays a thin orchestrator.
 */
function WindowHeader({ headerRegion, subheaderRegion, actions, onClose, chromeState }: WindowHeaderProps) {
    const { t } = useTranslation();

    // Chrome buttons get a gray background on mobile (`secondary`) to match
    // Procore mobile-web tap-target conventions. Desktop stays flat/text-only/
    // (`tertiary`). Consumer-provided action variants (e.g. `primary` for the
    // orange create button) always win over this default.
    const chromeButtonVariant: ChromeButtonVariant = chromeState.isMobile ? "secondary" : "tertiary";

    // Primary action is promoted to FAB in side panel mode — remove it from the
    // header so it doesn't render in both places.
    const pinnedActions = chromeState.isSidePanelOpen ? actions?.filter((a) => a.variant !== "primary") : actions;

    // Header border only when it actually separates two visible regions:
    //   - Content hidden: nothing below to separate from → no border.
    //   - Subheader present: subheader rows own their own separators
    //     (`borderBottom` by default) → no header border.
    //     Object Tree in particular sits search flush under the title.
    //   - Otherwise: header meets body directly → show border.
    // Mirrors WindowChrome's `showTitleBorder` logic.
    const showHeaderBorder = !chromeState.hideContent && !subheaderRegion;

    return (
        <>
            <Box
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.md,
                    // Mobile matches internal SidePanel's `PanelHeader` rhythm
                    // (SidePanel.tsx:42 — 12px vertical, 16px horizontal, min-height 48).
                    paddingTop: chromeState.isMobile ? 12 : SLOTTED_WINDOW_HEADER_PADDING,
                    paddingBottom: chromeState.isMobile ? 12 : SLOTTED_WINDOW_HEADER_PADDING,
                    paddingLeft: SLOTTED_WINDOW_HEADER_PADDING,
                    paddingRight: SLOTTED_WINDOW_HEADER_PADDING,
                    minHeight: chromeState.isMobile ? 48 : undefined,
                    borderBottom: showHeaderBorder ? HEADER_BORDER : undefined,
                    background: colors.white,
                }}
            >
                {/*
                  Consumer's headerRegion is wrapped in a flex-grow container
                  with `overflow: hidden` — that way any consumer JSX
                  (compound-helper or raw) is contained within its slot and
                  cannot overflow into the pinned buttons area on the right.
                  Long titles or wide content will be clipped visually rather
                  than pushing the [—][action?][×] cluster off-screen.
                */}
                <Box style={{ display: "flex", flex: 1, minWidth: 0, overflow: "hidden" }}>{headerRegion}</Box>
                <Box style={{ display: "flex", flexShrink: 0, gap: 4 }}>
                    {!chromeState.isDrawer && (
                        <Button
                            icon={
                                chromeState.isMobile ? (
                                    chromeState.isCollapsed ? (
                                        <CaretsOutVertical />
                                    ) : (
                                        <CaretsInVertical />
                                    )
                                ) : chromeState.isCollapsed ? (
                                    <ExpandIcon />
                                ) : (
                                    <MinimizeIcon />
                                )
                            }
                            variant={chromeButtonVariant}
                            size={chromeState.isMobile ? "md" : "sm"}
                            onClick={chromeState.toggleCollapse}
                            aria-label={chromeState.isCollapsed ? t("expand") : t("minimize")}
                            data-testid="window-minimize-toggle"
                        />
                    )}
                    {!chromeState.isDrawer && <PinnedActions actions={pinnedActions} isMobile={chromeState.isMobile} />}
                    {onClose && (
                        <Button
                            icon={<Clear />}
                            variant={chromeButtonVariant}
                            size={chromeState.isMobile ? "md" : "sm"}
                            onClick={onClose}
                            aria-label={t("close")}
                        />
                    )}
                </Box>
            </Box>
            {!chromeState.hideContent && subheaderRegion && (
                <Box style={{ background: colors.white }}>{subheaderRegion}</Box>
            )}
        </>
    );
}

/**
 * Thin orchestrator that wires the three moving parts together:
 *
 *  ┌─ Window (drag shell, loading/error/emptyState, docked/mobile branching) ──────┐
 *  │  ┌─ WindowHeader (chrome buttons + consumer slots) ───────────────────────┐   │
 *  │  │  [ headerRegion (consumer) ]  [ collapse ][ actions ][ close ]         │   │
 *  │  │  [ subheaderRegion (consumer, optional)                        ]       │   │
 *  │  └────────────────────────────────────────────────────────────────────────┘   │
 *  │  [ children (consumer body)                                               ]   │
 *  │  [ DrawerFab / SidePanelFab (primary action floated above panel chrome)    ]   │
 *  └────────────────────────────────────────────────────────────────────────────────┘
 *
 * Ownership:
 *  - Consumer owns: `headerRegion`, `subheaderRegion`, `children`, `actions` (data)
 *  - Novoweb owns: chrome buttons, action overflow, collapse/hide state, FAB promotion
 *
 * Backwards-compat: additive. `Window` and its `header` escape hatch remain
 * unchanged; existing consumers migrate on their own schedule.
 */
function SlottedWindowRoot({
    headerRegion,
    actions,
    subheaderRegion,
    children,
    onClose,
    position,
    loading,
    loadingMessage,
    error,
    emptyState,
    size,
    minSize,
    secondaryBgColor,
    autoHeight,
    chromeVariant = "full",
    "data-testid": dataTestId,
}: SlottedWindowProps) {
    const chromeState = useWindowChromeState();
    const isHeadless = chromeVariant === "headless";

    // Headless mode passes an empty (non-null) header so Window's renderHeader
    // takes the consumer-header branch and renders nothing, rather than falling
    // back to the legacy WindowChrome title bar.
    const header = isHeadless ? (
        <></>
    ) : (
        <WindowHeader
            headerRegion={headerRegion}
            subheaderRegion={subheaderRegion}
            actions={actions}
            onClose={onClose}
            chromeState={chromeState}
        />
    );

    // Feed the effective content-hidden state into Window so its contentOnly
    // branch hides the body when we're collapsed on desktop. Window requires
    // BOTH `minimized` and `onMinimizedChange` to override its default context
    // lookup. On mobile `hideContent` is always false, so Window never hides
    // the body — the outer SidePanelContainer handles shortening instead.
    const minimizeProps = chromeState.isDocked
        ? {}
        : {
              minimized: chromeState.hideContent,
              onMinimizedChange: chromeState.isMobile ? () => {} : chromeState.setLocalMinimized,
          };

    // In drawer and side panel mode, the primary action (variant: "primary") moves
    // out of the header and renders as a floating action button anchored above the
    // panel chrome. Non-primary actions in these modes are silently dropped for
    // the prototype — an RFD open question is whether they move to the
    // subheaderRegion, an overflow menu inside the body, or are dropped.
    const drawerFab =
        chromeState.isDrawerOpen && !isHeadless ? actions?.find((a) => a.variant === "primary") : undefined;
    const sidePanelFab =
        chromeState.isSidePanelOpen && !isHeadless ? actions?.find((a) => a.variant === "primary") : undefined;

    return (
        <SlottedWindowContext.Provider value={{ minimized: chromeState.hideContent }}>
            <Window
                position={position}
                header={header}
                loading={loading}
                loadingMessage={loadingMessage}
                error={error}
                emptyState={emptyState}
                size={size}
                minSize={minSize}
                secondaryBgColor={secondaryBgColor}
                autoHeight={autoHeight}
                data-testid={dataTestId}
                {...minimizeProps}
            >
                {children}
                {drawerFab && <DrawerFab action={drawerFab} />}
                {sidePanelFab && <SidePanelFab action={sidePanelFab} />}
            </Window>
        </SlottedWindowContext.Provider>
    );
}

const FAB_SHADOW = "0px 8px 16px -6px rgba(23, 26, 28, 0.24), 0px 4px 4px 0px rgba(0, 0, 0, 0.08)";

/** Button (or button + dropdown menu) rendered inside a FAB wrapper. */
function FabContent({ action }: { action: SlottedWindowAction }) {
    const isMenu = "items" in action && action.items;
    const button = (
        <Button
            icon={action.icon}
            variant="primary"
            size="lg"
            onClick={isMenu ? undefined : action.onClick}
            disabled={action.disabled}
            aria-label={action.label}
            data-testid={action["data-testid"]}
            {...(isMenu ? { "aria-haspopup": "menu" as const } : {})}
        />
    );
    if (!isMenu) {
        return button;
    }
    return (
        <OverlayTrigger
            trackAriaExpanded
            overlay={
                <Card>
                    <Menu>
                        <Menu.Options>
                            {action.items!.map((it, i) => (
                                <Menu.Item item={null} key={i} onClick={it.onClick}>
                                    {it.label}
                                </Menu.Item>
                            ))}
                        </Menu.Options>
                    </Menu>
                </Card>
            }
            placement="top-right"
            trigger="click"
            restoreFocusOnHide="react-aria-focus-scope"
        >
            {button}
        </OverlayTrigger>
    );
}

/**
 * Drawer FAB — portaled to `document.body` with `position: fixed` so it
 * escapes the drawer's `transform` slide animation, which would otherwise
 * scope `position: fixed` to the transformed subtree.
 */
function DrawerFab({ action }: { action: SlottedWindowAction }) {
    return createPortal(
        <Box
            style={{
                position: "fixed",
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                right: "calc(env(safe-area-inset-right, 0px) + 24px)",
                zIndex: DRAWER_Z_INDEX + 1,
                boxShadow: FAB_SHADOW,
            }}
        >
            <FabContent action={action} />
        </Box>,
        document.body,
    );
}

/**
 * Side panel FAB — portaled to the `PanelContainer` element (provided via
 * `SidePanelFabTargetContext`) with `position: absolute` so the FAB sits
 * inside the panel and moves with it when the panel changes height.
 */
function SidePanelFab({ action }: { action: SlottedWindowAction }) {
    const panelEl = useContext(SidePanelFabTargetContext);
    if (!panelEl) {
        return null;
    }
    return createPortal(
        <Box
            style={{
                position: "absolute",
                bottom: 24,
                right: 24,
                zIndex: 10,
                boxShadow: FAB_SHADOW,
            }}
        >
            <FabContent action={action} />
        </Box>,
        panelEl,
    );
}

/**
 * Renders the between-collapse-and-close action slot. Enforces the design cap:
 * one custom action inline, everything else overflows into a single kebab menu.
 */
function PinnedActions({ actions, isMobile }: { actions: SlottedWindowAction[] | undefined; isMobile: boolean }) {
    if (!actions || actions.length === 0) {
        return null;
    }
    if (actions.length === 1) {
        return <InlineAction action={actions[0]} isMobile={isMobile} />;
    }
    return <OverflowActions actions={actions} isMobile={isMobile} />;
}

function InlineAction({ action, isMobile }: { action: SlottedWindowAction; isMobile: boolean }) {
    const isMenu = "items" in action && action.items;
    const defaultVariant = isMobile ? "secondary" : "tertiary";
    const button = (
        <Button
            icon={action.icon}
            variant={action.variant ?? defaultVariant}
            size={isMobile ? "md" : "sm"}
            onClick={isMenu ? undefined : action.onClick}
            disabled={action.disabled}
            aria-label={action.label}
            // Match Panel.HeaderActions' pattern for menu triggers (Panel.js:83-90):
            // announces the button as a menu opener + lets OverlayTrigger manage
            // aria-expanded. Also silences the spurious `:focus-visible` ring on
            // mount that appeared before we adopted this pattern.
            {...(isMenu ? { "aria-haspopup": "menu" as const } : {})}
            data-testid={action["data-testid"]}
        />
    );

    if (isMenu) {
        return (
            <OverlayTrigger
                overlay={
                    <Card>
                        <Menu>
                            <Menu.Options>
                                {action.items!.map((it, i) => (
                                    <Menu.Item item={null} key={i} onClick={it.onClick}>
                                        {it.label}
                                    </Menu.Item>
                                ))}
                            </Menu.Options>
                        </Menu>
                    </Card>
                }
                placement="bottom-right"
                trigger="click"
                trackAriaExpanded
                // Prevents the mount-time focus() call in OverlayTrigger's
                // "restore focus after hide" effect (OverlayTrigger.js — the
                // effect fires on initial mount because visibility.isVisible
                // starts `false`). "react-aria-focus-scope" delegates restore
                // to FocusScope which only fires on actual overlay close, not
                // on component mount.
                restoreFocusOnHide="react-aria-focus-scope"
            >
                {button}
            </OverlayTrigger>
        );
    }
    return button;
}

/**
 * Renders every action as a flat menu item behind a single kebab button. Nested
 * menu-actions (with `items`) are flattened — their items appear inline at the
 * top level. Multi-level nesting can be added later if design asks for it.
 */
function OverflowActions({ actions, isMobile }: { actions: SlottedWindowAction[]; isMobile: boolean }) {
    const { t } = useTranslation();
    const flatItems = actions.flatMap<SlottedWindowMenuItem>((a) =>
        "items" in a && a.items ? a.items : [{ label: a.label, onClick: a.onClick! }],
    );
    const button = (
        <Button
            icon={<EllipsisVertical />}
            variant={isMobile ? "secondary" : "tertiary"}
            size={isMobile ? "md" : "sm"}
            aria-label={t("moreActions")}
            aria-haspopup="menu"
            data-testid="window-actions-overflow"
        />
    );
    return (
        <OverlayTrigger
            trackAriaExpanded
            overlay={
                <Card>
                    <Menu>
                        <Menu.Options>
                            {flatItems.map((it, i) => (
                                <Menu.Item item={null} key={i} onClick={it.onClick}>
                                    {it.label}
                                </Menu.Item>
                            ))}
                        </Menu.Options>
                    </Menu>
                </Card>
            }
            placement="bottom-right"
            trigger="click"
            restoreFocusOnHide="react-aria-focus-scope"
        >
            {button}
        </OverlayTrigger>
    );
}

// ============================================================================
// Compound-component helpers
//
// Opt-in building blocks that give consumers the design-approved default look
// with minimal composition. Consumers can also skip these and pass raw JSX in
// `headerRegion` / `subheaderRegion` for exotic patterns.
// ============================================================================

/** Flex row for header content — puts children in a row with title-grow. */
function HeaderRow({ children }: { children: ReactNode }) {
    return (
        <Box
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: spacing.sm,
                flex: 1,
                minWidth: 0,
            }}
        >
            {children}
        </Box>
    );
}
HeaderRow.displayName = "SlottedWindow.HeaderRow";

interface TitleProps {
    children: ReactNode;
    /**
     * Optional secondary line rendered below the primary title in small gray text.
     * Both title and subtitle truncate with an ellipsis when they exceed one line.
     * Hidden when the window is minimized (single-line header).
     * Example: Properties uses this to show the filename below the object name.
     */
    subtitle?: ReactNode;
}

const titleLineStyle: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

/**
 * A single title/subtitle line that shows its full text in a tooltip when
 * the ellipsis-truncated `Typography` no longer fits its container — e.g.
 * long property names or federated-model file names in Properties' header.
 * Always wraps in `Tooltip`, but only supplies overlay content once
 * `useIsTruncated` confirms the text actually overflows.
 */
function TitleLine({
    children,
    ...typographyProps
}: { children: ReactNode } & Pick<ComponentProps<typeof Typography>, "intent" | "weight" | "color" | "style">) {
    const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>(children);
    return (
        <Tooltip overlay={isTruncated ? <Tooltip.Content>{children}</Tooltip.Content> : <span></span>} placement="top">
            <Typography ref={ref} {...typographyProps}>
                {children}
            </Typography>
        </Tooltip>
    );
}

function Title({ children, subtitle }: TitleProps) {
    const { minimized } = useContext(SlottedWindowContext);
    return (
        <Box style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <TitleLine intent="h3" weight="semibold" style={titleLineStyle}>
                {children}
            </TitleLine>
            {subtitle && !minimized && (
                <TitleLine
                    intent="small"
                    weight="semibold"
                    color="gray45"
                    style={{ ...titleLineStyle, marginTop: spacing.xs }}
                >
                    {subtitle}
                </TitleLine>
            )}
        </Box>
    );
}
Title.displayName = "SlottedWindow.Title";

function BackButton({
    onClick,
    "data-testid": testid = "slotted-window-back-button",
}: {
    onClick: () => void;
    "data-testid"?: string;
}) {
    const { t } = useTranslation();
    return (
        <Button
            icon={<ChevronLeft />}
            variant="tertiary"
            size="sm"
            onClick={onClick}
            aria-label={t("goBack")}
            data-testid={testid}
        />
    );
}
BackButton.displayName = "SlottedWindow.BackButton";

/**
 * Row for tab bars in the subheaderRegion. Only applies horizontal padding — Tabs.Tab
 * has its own vertical rhythm via the underline, so extra vertical padding
 * pushes them out of alignment.
 */
function TabsRow({ children }: { children: ReactNode }) {
    return (
        <Box
            style={{
                paddingLeft: SLOTTED_WINDOW_HEADER_PADDING,
                paddingRight: SLOTTED_WINDOW_HEADER_PADDING,
                borderBottom: HEADER_BORDER,
            }}
        >
            {children}
        </Box>
    );
}
TabsRow.displayName = "SlottedWindow.TabsRow";

interface SubheaderRowProps {
    children: ReactNode;
    /** Separator below this row. On by default. */
    borderBottom?: boolean;
    /** Override the default padding/layout styles. */
    style?: CSSProperties;
}

/** Padded/bordered row for search, filter chips, and other subheaderRegion content. */
function SubheaderRow({ children, borderBottom = true, style }: SubheaderRowProps) {
    return (
        <Box
            style={{
                paddingTop: spacing.sm,
                paddingBottom: spacing.sm,
                paddingLeft: SLOTTED_WINDOW_HEADER_PADDING,
                paddingRight: SLOTTED_WINDOW_HEADER_PADDING,
                borderBottom: borderBottom ? HEADER_BORDER : undefined,
                ...style,
            }}
        >
            {children}
        </Box>
    );
}
SubheaderRow.displayName = "SlottedWindow.SubheaderRow";

/**
 * Subheader row that paints a blue background when `active` — for
 * selection-state rows (Object Tree bulk actions).
 */
function BulkActionsRow({ active, children }: { active: boolean; children: ReactNode }) {
    return (
        <Box
            style={{
                paddingTop: spacing.sm,
                paddingBottom: spacing.sm,
                paddingLeft: SLOTTED_WINDOW_HEADER_PADDING,
                paddingRight: SLOTTED_WINDOW_HEADER_PADDING,
                display: "flex",
                alignItems: "center",
                gap: spacing.sm,
                backgroundColor: active ? colors.blue96 : colors.white,
                borderBottom: HEADER_BORDER,
                transition: "background-color 0.2s ease",
            }}
        >
            {children}
        </Box>
    );
}
BulkActionsRow.displayName = "SlottedWindow.BulkActionsRow";

export const SlottedWindow = Object.assign(SlottedWindowRoot, {
    HeaderRow,
    Title,
    BackButton,
    TabsRow,
    SubheaderRow,
    BulkActionsRow,
});
